
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Flag, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthModalSignupForm } from "@/components/auth/AuthModalSignupForm";
import { AuthModalGoogleButton } from "@/components/auth/AuthModalGoogleButton";
import { AuthModalDivider } from "@/components/auth/AuthModalDivider";
import { Spinner } from "@/components/ui/spinner";

const Signup = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Get the redirect path properly from location state
  const from = location.state?.from || "/";

  // Debug output for troubleshooting
  console.log("Signup component: Auth state:", { 
    isLoading: loading, 
    hasUser: !!user,
    userEmail: user?.email,
    redirectTarget: from,
    shouldRedirect
  });

  // Handle redirection if user is already authenticated AND we've triggered a redirect
  useEffect(() => {
    if (!loading && user && shouldRedirect) {
      console.log("Signup: User is logged in and redirect triggered, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from, shouldRedirect]);

  const handleSignupSuccess = () => {
    console.log("Signup: Setting shouldRedirect flag after successful signup");
    setShouldRedirect(true);
    // The redirect will be handled by the useEffect above
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const handleToggleLogin = () => {
    // Preserve the redirect target when switching to login
    navigate('/login', { state: { from } });
  };

  // Show loading state while checking authentication
  if (loading) {
    console.log("Signup: Showing loading spinner (auth state determining)");
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="h-8 w-8 text-redflags-red" />
      </div>
    );
  }

  // If we know the user is authenticated AND we've triggered a redirect,
  // show loading state before redirect completes
  if (user && shouldRedirect) {
    console.log("Signup: User authenticated and redirect triggered, showing loading spinner");
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="h-8 w-8 text-redflags-red" />
      </div>
    );
  }

  console.log("Signup: Rendering signup form");
  // Render the signup form whether the user is authenticated or not
  // This allows users to create a new account if needed
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <div className="p-2 bg-redflags-red/10 rounded-full">
                <Flag className="w-8 h-8 text-redflags-red" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-redflags-dark">
              Create an account
            </h1>
            <p className="text-redflags-gray mt-2">
              Join RedFlags to share your experiences
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <AuthModalSignupForm onSuccess={handleSignupSuccess} onClose={() => {}} />

            <AuthModalDivider />

            <AuthModalGoogleButton className="w-full mt-4" />
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              <span>Already have an account?</span>{' '}
              <Button variant="link" className="p-0 h-auto" onClick={handleToggleLogin}>
                Log in
              </Button>
            </p>
          </div>
          
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={handleReturnHome}
            >
              <Home className="w-4 h-4" />
              Return to Home
            </Button>
          </div>
          
          <footer className="mt-10 pt-6 text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} RedFlags. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Signup;
